import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomToast from '../../Toast';
import LanguageSelectorModal from '../../pages/modal/languageSelectorModal';
import Loader from '../../layouts/loader';

const MicroModule = () => {
    const [microCourseList, setMicroCourseList] = useState([]);
    const [completedMicroCourses, setCompletedMicroCourses] = useState([]);
    const [languageList, setLanguageList] = useState([]);
    const [loader, setLoader] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);

    const { i18n } = useTranslation();
    const { t } = useTranslation();
    // const currentLang = course?.lang;

    const getLangCode = (lang) => {
        switch (lang) {
            case 'English':
                return 'en';
            case 'Hindi':
                return 'hi';
            case 'Marathi':
                return 'mar';
            default:
                return 'en';
        }
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            window.scrollTo(0, 0);
        });
        fetchMicroCourseList();
        fetchCompletedCourses();
    }, []);

    const fetchMicroCourseList = async () => {
        try {
            const token = apiService.getAuthToken()
            const orgInfo = JSON.parse(localStorage.getItem("orgInfo"));
            const result = await apiService.getLmsv1(`getMicroModuleCourseList/${orgInfo?.org_id}`, token);
            if (result) {
                setMicroCourseList(result?.data || []);
                const languages = result?.data[0]?.languageList || [];
                setLanguageList(languages);

                // If only one language is available, automatically select it
                if (languages.length === 1) {
                    const singleLanguage = languages[0];
                    const selected_lang = JSON.stringify({
                        id: singleLanguage.id || microCourseList?.lang_id,
                        name: singleLanguage.name || microCourseList?.lang
                    });
                    localStorage.setItem('selectedLanguage', selected_lang);
                }

                console.log("Micro Course List:", result?.data);
                setLoader(false);
            }
        } catch (error) {
            setError('Error fetching user data');
            console.error(error);
            setLoader(false);
        }
    };

    const handleConfirmLanguage = (languageId, courseData = null) => {
        // Find the selected language from languageList
        const selectedLang = languageList.find(lang => lang.id === languageId);

        if (!selectedLang) {
            CustomToast.error('Please select a valid language');
            return;
        }

        // Store the selected language as JSON string
        const languageData = JSON.stringify({
            id: selectedLang.id,
            name: selectedLang.name
        });

        localStorage.setItem('selectedLanguage', languageData);
        setShowModal(false);

        // Start the course with the selected course or the first course if none provided
        let courseToStart = null;

        if (courseData && (courseData.course_meta_id || courseData.id)) {
            // If courseData was passed in and has necessary ID, use it
            courseToStart = courseData;
        } else if (microCourseList.length > 0 && microCourseList[0].courseList) {
            // Fall back to the first course in the list
            courseToStart = microCourseList[0].courseList;
        }

        if (courseToStart) {
            startCourse(courseToStart);
        } else {
            CustomToast.error('No valid course data available');
        }
    };

    const startCourse = async (data) => {

        console.log("Starting course with data:", data);

        const userDetails = JSON.parse(sessionStorage.getItem('userDetail'));
        let orgInfo = JSON.parse(localStorage.getItem("orgInfo"));

        // Check if we have a selected language
        const selectedLanguage = localStorage.getItem('selectedLanguage');

        // If no language is selected and we have multiple languages, show the modal
        if (!selectedLanguage && languageList.length > 1) {
            setShowModal(true);
            return;
        }

        // If no language is selected but we only have one language option, use it
        if (!selectedLanguage && languageList.length === 1) {
            const singleLanguage = languageList[0];
            const languageData = JSON.stringify({
                id: singleLanguage.id,
                name: singleLanguage.name
            });
            localStorage.setItem('selectedLanguage', languageData);
            // console.log("Selected language set to:", singleLanguage.id);
        }

        // Extract the course_meta_id - this is the ID we need for the URL path
        const courseMetaId = data?.course_meta_id;
        const courseId = data?.course_id;

        localStorage.setItem('microCourseId', courseId);

        if (!courseMetaId) {
            CustomToast.error('Course information is missing');
            return;
        }

        // Determine the last module ID or the first module ID to use as a parameter
        let lastModuleId = null;

        // If this is a completed course with modules data
        if (data.modules && Array.isArray(data.modules) && data.modules.length > 0) {
            // Try to find the last incomplete module
            const lastIncompleteModule = data.modules.find(module => !module.is_completed);
            if (lastIncompleteModule) {
                lastModuleId = lastIncompleteModule.module_id;
            } else {
                // If all modules are complete, use the first module
                lastModuleId = data.modules[0].module_id;
            }

        }
        // If we don't have modules in the current data object, try the microCourseList
        else if (microCourseList && microCourseList.length > 0) {
            // Find the matching course in the microCourseList
            const matchingCourse = microCourseList.find(course =>
                course.courseList && (course.courseList.course_meta_id === courseMetaId || course.courseList.id === courseMetaId)
            );

            if (matchingCourse && matchingCourse.modules && matchingCourse.modules.length > 0) {
                // Try to find the first incomplete module in this course
                const firstIncompleteModule = matchingCourse.modules.find(module => !module.is_completed);
                if (firstIncompleteModule) {
                    lastModuleId = firstIncompleteModule.module_id;
                } else {
                    // Default to the first module if all are complete
                    lastModuleId = matchingCourse.modules[0].module_id;
                }
            }
        }

        try {
            const token = apiService.getAuthToken();

            // Prepare payload for API
            const payload = {
                org_id: orgInfo?.org_id,
                email: userDetails?.email,
                user_id: userDetails?.uid,
                micro_course_id: data?.course_id,
                lang_id: JSON.parse(selectedLanguage)?.id,
                // module_id: data?.module_id || "686bac5f60cac39e9f7cf242", // Default to first module if not provided
                // module_content_id: "686bac6f60cac39e9f7cf24e",
                // module_status: "pending",
                course_status: "inprogress",
                // last_module_id: data?.last_module_id || lastModuleId || data?.module_id || "686bac5f60cac39e9f7cf242",
            };

            console.log('Start module:', payload);

            // Send the completion status to server
            const result = await apiService.postLms("saveUserMicroCourseAns", payload, token);

            if (result && result.success) {

                console.log('Module started successfully:', result);

                // Navigate using location.state to pass course data
                navigate(`/micro_module/course_modules/${courseMetaId}`, {
                    state: {
                        course: {
                            course_id: data.course_id,
                            course_name: data.course_name || data.title,
                            lang_id: data?.lang_id,
                            lang: data?.lang,

                            //   duration: data.duration,
                            //   module_count: data.module_count,
                            course_meta_id: data.course_meta_id,
                            //   description: data.description,
                            //   image: data.image,
                            //   title: data.title,
                            //   modules: data.modules,
                            course_status: data.course_status
                        }
                    }
                });

                return true;
            } else {
                console.error('Failed to sync module completion with server');
                return false;
            }
        } catch (error) {
            console.error('Error syncing module completion:', error);
            return false;
        }

    }

    const continueCourse = async (data) => {

        const userDetails = JSON.parse(sessionStorage.getItem('userDetail') || JSON.parse(localStorage.getItem('user_details')));
        let orgInfo = JSON.parse(localStorage.getItem("orgInfo"));

        // Check if we have a selected language
        const selectedLanguage = localStorage.getItem('selectedLanguage');

        // If no language is selected and we have multiple languages, show the modal
        if (!selectedLanguage && languageList.length > 1) {
            setShowModal(true);
            return;
        }

        // If no language is selected but we only have one language option, use it
        if (!selectedLanguage && languageList.length === 1) {
            const singleLanguage = languageList[0];
            const languageData = JSON.stringify({
                id: singleLanguage.id,
                name: singleLanguage.name
            });
            localStorage.setItem('selectedLanguage', languageData);
            // console.log("Selected language set to:", singleLanguage.id);
        }

        // Extract the course_meta_id - this is the ID we need for the URL path
        const courseMetaId = data?.course_meta_id;
        const courseId = data?.course_id;

        localStorage.setItem('microCourseId', courseId);

        if (!courseMetaId) {
            CustomToast.error('Course information is missing');
            return;
        }

        // Determine the last module ID or the first module ID to use as a parameter
        let lastModuleId = null;

        // If this is a completed course with modules data
        if (data.modules && Array.isArray(data.modules) && data.modules.length > 0) {
            // Try to find the last incomplete module
            const lastIncompleteModule = data.modules.find(module => !module.is_completed);
            if (lastIncompleteModule) {
                lastModuleId = lastIncompleteModule.module_id;
            } else {
                // If all modules are complete, use the first module
                lastModuleId = data.modules[0].module_id;
            }

        }
        // If we don't have modules in the current data object, try the microCourseList
        else if (microCourseList && microCourseList.length > 0) {
            // Find the matching course in the microCourseList
            const matchingCourse = microCourseList.find(course =>
                course.courseList && (course.courseList.course_meta_id === courseMetaId || course.courseList.id === courseMetaId)
            );

            if (matchingCourse && matchingCourse.modules && matchingCourse.modules.length > 0) {
                // Try to find the first incomplete module in this course
                const firstIncompleteModule = matchingCourse.modules.find(module => !module.is_completed);
                if (firstIncompleteModule) {
                    lastModuleId = firstIncompleteModule.module_id;
                } else {
                    // Default to the first module if all are complete
                    lastModuleId = matchingCourse.modules[0].module_id;
                }
            }
        }

        // Navigate using location.state to pass course data
        navigate(`/micro_module/course_modules/${courseMetaId}`, {
            state: {
                course: {
                    course_id: data.course_id,
                    course_name: data.course_name || data.title,
                    lang_id: data?.lang_id,
                    lang: data?.lang,
                    //   duration: data.duration,
                    //   module_count: data.module_count,
                    course_meta_id: data.course_meta_id,
                    //   description: data.description,
                    //   image: data.image,
                    //   title: data.title,
                    //   modules: data.modules,
                    course_status: data.course_status
                }
            }
        });

    }

    const handleDownloadCertificate = async (courseId) => {
        const userDetails = JSON.parse(sessionStorage.getItem('userDetail'));
        // let data = {
        //     "course_id":
        //         courseDetails?.course_id,
        //     "send_email": true,
        //     "user_id": JSON.parse(user_details)?.uid,
        // course_name: courseDetails?.course_name,
        // course_lang: localStorage.getItem('selectedLanguage')
        // }

        setLoader(true);
        try {
            const token = apiService.getAuthToken()
            const result = await apiService.get(`generate-micro-course-certificate?user_id=${userDetails?.uid}&course_id=${courseId}`, token)
            if (result) {
                setLoader(false);
                window.open(result?.data?.certificate_url, "_blank");
            } else {
                setLoader(false);
                CustomToast.error('Something went wrong');
            }
        } catch (error) {
            setLoader(false);
            console.error(error);
            CustomToast.error('Something went wrong');
        }
    };

    const fetchCompletedCourses = async () => {
        try {
            const token = apiService.getAuthToken()
            const result = await apiService.getLmsv1(`getUserCompletedMicroCourseList`, token);
            if (result && result.status == true) {
                setCompletedMicroCourses(result.data || []);
            }
        } catch (error) {
            console.error('Error fetching completed micro courses:', error);
        }
    };

    return (
        <>
            <div className='page__main'>
                <div className="dashboard" style={{ minHeight: '100vh' }}>
                    <div className='container-fluid'>
                        <p className='mb-4'><h3 className="fw-semibold font-26 mb-2">Micro-Module</h3>
                            <small>Quarterly updates and learning on your role as an IC member</small></p>

                        {microCourseList && microCourseList?.length > 0 ? (
                            <div>
                                <h6 className='fw-bold'>Ongoing Micro-Module</h6>

                                {microCourseList.map((courseWrapper, index) => {
                                    const course = courseWrapper.courseList;
                                    const langId = getLangCode(course?.lang);
                                    const tForLang = i18n.getFixedT(langId);
                                    return (
                                        <>
                                            <div className="card mb-4 shadow-sm p-3" key={course._id || index}>
                                                <div className="row d-flex align-items-center justify-content-between">
                                                    <div className='col-4'>
                                                        <img
                                                            src={course.image || "/images/Layer_2.svg"}
                                                            alt="logo"
                                                            style={{ width: '100%', height: '220px', objectFit: 'cover' }}
                                                            className="img-fluid"
                                                        />
                                                    </div>
                                                    <div className='col-6'>
                                                        <h4 className="fw-bold fs-5 mb-2 mt-0">{course.title}</h4>
                                                        <p className='mb-4'>
                                                            <span className="text-dark-gray text-capitalize pe-4">
                                                                <img src="/images/time.svg" className="me-0" /> {course.duration}
                                                            </span>
                                                            <span className="text-dark-gray text-capitalize">
                                                                <img src="/images/modules.png" className="me-2" />
                                                                {course.module_count || 0} {tForLang('home_page_modules')}
                                                            </span>
                                                        </p>
                                                        <span className="text-muted small mb-4" dangerouslySetInnerHTML={{ __html: course.description }}></span>
                                                        <div className='row mt-5'>
                                                            <div className='col-6'>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-md btn-primary disabled-btn w-100"
                                                                    onClick={() => {
                                                                        // Save course title in localStorage with course-specific key
                                                                        const courseNameKey = `courseName_${course.course_meta_id}`;
                                                                        localStorage.setItem(courseNameKey, course.title);

                                                                        // Make sure the course has necessary ID
                                                                        if (!course.course_meta_id) {
                                                                            CustomToast.error('Course information is missing');
                                                                            return;
                                                                        }

                                                                        // If only one language available, start directly
                                                                        if (languageList.length === 1) {
                                                                            startCourse(course);
                                                                        } else {
                                                                            // Show language selector modal
                                                                            setShowModal(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    {course.course_status === "notstarted" ? "Get Started" : "Continue Course"}
                                                                </button>

                                                            </div>
                                                            <div className='col-6'></div>
                                                        </div>
                                                    </div>
                                                    <div className='col-2'></div>
                                                </div>
                                            </div>
                                            {/* Modal */}
                                            <LanguageSelectorModal
                                                show={showModal}
                                                onClose={() => setShowModal(false)}
                                                languageList={languageList}
                                                onConfirm={(languageId) => handleConfirmLanguage(languageId, course)}
                                            />
                                        </>
                                    );
                                })}

                            </div>
                        ) : (
                        <>
                            <h6 className='fw-bold mb-3'>Ongoing Micro-Module</h6>
                            < div className="card mb-4 shadow-sm p-5 text-center" style={{ background: '#F8F8F8', borderRadius: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <img
                                        src="/images/no-modules.svg"
                                        alt="No Modules"
                                        style={{ width: '60px', marginBottom: '16px', opacity: 0.7 }}
                                    />
                                </div>
                                <h6 className="fw-bold mb-1">No Micro Modules Available</h6>
                                <p className="text-muted mb-0">
                                    There are no active micro-modules at the moment.<br />
                                    You’ll be notified as soon as the next one is released.
                                </p>
                            </div>
                        </>
                        )}

                        {loader ? <Loader /> : <>

                            {completedMicroCourses && completedMicroCourses.length > 0 && (
                                <div className='mt-6'>
                                    <h6 className='fw-bold'>Completed Micro-Module</h6>

                                    {completedMicroCourses?.map((courseWrapper, index) => {
                                        const completedMicroCourse = courseWrapper?.courseList;
                                        const langId = getLangCode(completedMicroCourse?.lang);
                                        const tForLang = i18n.getFixedT(langId);
                                        return (
                                            <div className="card mb-4 shadow-sm p-3" key={completedMicroCourse?.id}>
                                                <div className="row d-flex align-items-center justify-content-between">
                                                    <div className='col-4'>
                                                        <img
                                                            src={completedMicroCourse?.image || "/images/Layer_2.svg"}
                                                            alt="logo"
                                                            style={{ width: '100%', height: '220px', objectFit: 'cover' }}
                                                            className="img-fluid"
                                                        />
                                                    </div>
                                                    <div className='col-6'>
                                                        <h4 className="fw-bold fs-5 mb-2 mt-0">{completedMicroCourse?.course_name}</h4>
                                                        <p className='mb-4'>
                                                            <span className="text-dark-gray text-capitalize pe-4">
                                                                <img src="/images/time.svg" className="me-0" /> {completedMicroCourse?.duration}
                                                            </span>
                                                            <span className="text-dark-gray text-capitalize">
                                                                <img src="/images/modules.png" className="me-2" />{completedMicroCourse?.module_count} {tForLang('home_page_modules')}
                                                            </span>
                                                        </p>
                                                        <span className="text-muted small mb-4" dangerouslySetInnerHTML={{ __html: completedMicroCourse?.description }}></span>
                                                        <div className='row mt-5'>
                                                            <div className='col-6'>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-md btn-secondary w-100 mb-lg-0 mb-3"
                                                                    onClick={() => {
                                                                        // Save course title in localStorage with course-specific key
                                                                        const courseNameKey = `courseName_${completedMicroCourse?.course_meta_id}`;
                                                                        localStorage.setItem(courseNameKey, completedMicroCourse?.title);

                                                                        // Ensure the course has a course_meta_id or id
                                                                        if (!completedMicroCourse?.course_meta_id) {
                                                                            CustomToast.error('Course information is missing');
                                                                            return;
                                                                        }

                                                                        continueCourse(completedMicroCourse);
                                                                    }}
                                                                >
                                                                    {tForLang('home_page_view_modules')}
                                                                </button>

                                                            </div>

                                                            <div className='col-6'>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDownloadCertificate(completedMicroCourse?.course_id)}
                                                                    className="btn btn-md btn-primary w-100"
                                                                >
                                                                    {tForLang('home_page_download_certificate')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='col-2'></div>
                                                </div>
                                            </div>

                                        );
                                    })}

                                </div>)} </>}
                    </div>
                </div>
            </div >
        </>
    );

}

export default MicroModule;